import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Shield, Settings as SettingsIcon } from "lucide-react";
import { CATEGORIES, getConsent, saveConsent } from "@/lib/consent";

const ALL_ON = { essential: true, functional: true, analytics: true, marketing: true };
const ALL_OFF = { essential: true, functional: false, analytics: false, marketing: false };

export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [draft, setDraft] = useState(ALL_OFF);

  // Initial mount: decide whether to show the banner
  useEffect(() => {
    const existing = getConsent();
    if (!existing) {
      setOpen(true);
    }
    const reopen = () => {
      const c = getConsent();
      setDraft(c ? { ...ALL_OFF, ...c, essential: true } : ALL_OFF);
      setOpen(true);
      setShowSettings(true);
    };
    window.addEventListener("mbw:open-cookie-settings", reopen);
    return () => window.removeEventListener("mbw:open-cookie-settings", reopen);
  }, []);

  if (!open) return null;

  const accept = (choice) => {
    saveConsent(choice);
    setOpen(false);
    setShowSettings(false);
  };

  return (
    <>
      {/* Backdrop for settings modal */}
      {showSettings && (
        <div
          className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm"
          onClick={() => setShowSettings(false)}
          aria-hidden="true"
        />
      )}

      {/* Main banner — bottom-anchored */}
      <div
        className="fixed bottom-4 left-4 right-4 md:left-6 md:right-6 z-[110] flex justify-center pointer-events-none"
        role="dialog"
        aria-labelledby="cookie-banner-title"
        aria-describedby="cookie-banner-desc"
        data-testid="cookie-banner"
      >
        <div className="pointer-events-auto w-full max-w-3xl bg-[#1A2433] text-white rounded-2xl shadow-2xl border border-slate-700 overflow-hidden">
          {/* Compact banner */}
          {!showSettings && (
            <div className="p-5 md:p-6 flex gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#991B1B] flex items-center justify-center flex-shrink-0">
                <Cookie className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 id="cookie-banner-title" className="font-heading font-bold text-base md:text-lg mb-1">
                  Wir respektieren Ihre Privatsphäre
                </h2>
                <p id="cookie-banner-desc" className="text-sm text-slate-300 leading-relaxed">
                  Wir nutzen Cookies, um Ihnen die bestmögliche Erfahrung zu bieten und die Nutzung
                  unserer Seite zu analysieren. Technisch notwendige Cookies sind immer aktiv.
                  Weitere Informationen in unserer{" "}
                  <Link to="/datenschutz" className="underline hover:no-underline text-white">Datenschutzerklärung</Link>.
                </p>
                <div className="flex flex-wrap gap-2 md:gap-3 mt-4">
                  <button
                    onClick={() => accept(ALL_ON)}
                    className="px-4 py-2 rounded-lg bg-[#991B1B] hover:bg-[#7c1414] font-semibold text-sm transition"
                    data-testid="cookie-accept-all"
                  >
                    Alle akzeptieren
                  </button>
                  <button
                    onClick={() => accept(ALL_OFF)}
                    className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 font-semibold text-sm transition"
                    data-testid="cookie-reject-all"
                  >
                    Nur notwendige
                  </button>
                  <button
                    onClick={() => {
                      const c = getConsent();
                      setDraft(c ? { ...ALL_OFF, ...c, essential: true } : ALL_OFF);
                      setShowSettings(true);
                    }}
                    className="px-4 py-2 rounded-lg text-white/80 hover:text-white text-sm inline-flex items-center gap-1.5 transition"
                    data-testid="cookie-settings-open"
                  >
                    <SettingsIcon className="w-4 h-4" /> Einstellungen
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Settings modal */}
          {showSettings && (
            <div className="max-h-[80vh] flex flex-col" data-testid="cookie-settings-modal">
              <div className="p-5 md:p-6 flex items-start justify-between gap-4 border-b border-slate-700">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#991B1B] flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="font-heading font-bold text-lg md:text-xl">Cookie-Einstellungen</h2>
                    <p className="text-xs text-slate-400 mt-0.5">Steuern Sie selbst, welche Kategorien Sie zulassen.</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-slate-400 hover:text-white p-1"
                  aria-label="Schliessen"
                  data-testid="cookie-settings-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5 md:p-6 overflow-y-auto space-y-4">
                {CATEGORIES.map((cat) => {
                  const enabled = !!draft[cat.key];
                  return (
                    <div
                      key={cat.key}
                      className="bg-slate-800/50 border border-slate-700 rounded-xl p-4"
                      data-testid={`cookie-cat-${cat.key}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-white">{cat.label}</div>
                          <p className="text-xs text-slate-400 leading-relaxed mt-1">{cat.description}</p>
                        </div>
                        <label className="flex-shrink-0 cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={enabled}
                            disabled={cat.required}
                            onChange={(e) => setDraft({ ...draft, [cat.key]: e.target.checked })}
                            data-testid={`cookie-toggle-${cat.key}`}
                          />
                          <span
                            className={`block w-11 h-6 rounded-full transition relative ${
                              cat.required ? "bg-[#991B1B]/70 cursor-not-allowed" : enabled ? "bg-[#991B1B]" : "bg-slate-600"
                            }`}
                          >
                            <span
                              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                                enabled ? "translate-x-5" : ""
                              }`}
                            />
                          </span>
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="p-5 md:p-6 border-t border-slate-700 flex flex-wrap gap-3 justify-end">
                <button
                  onClick={() => accept(ALL_OFF)}
                  className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-semibold"
                  data-testid="cookie-settings-reject-all"
                >
                  Nur notwendige
                </button>
                <button
                  onClick={() => accept(draft)}
                  className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-semibold"
                  data-testid="cookie-settings-save"
                >
                  Auswahl speichern
                </button>
                <button
                  onClick={() => accept(ALL_ON)}
                  className="px-4 py-2 rounded-lg bg-[#991B1B] hover:bg-[#7c1414] text-sm font-semibold"
                  data-testid="cookie-settings-accept-all"
                >
                  Alle akzeptieren
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
